/**
 * The support queue, across every workspace (CLAUDE.md 12.2, 12.3).
 *
 * The default tab is "needs reply", not "open". Those are different questions: an
 * in-progress ticket the reporter has just answered is waiting on us again, and an
 * open one somebody has assigned to themselves may not be. The counts on the tabs
 * come from one summary call so the size of each pile is visible before it is
 * opened.
 *
 * Nothing is edited in the table. Status, priority and assignee live on the ticket
 * page next to the thread they describe -- three inline selects per row made every
 * row a form and every misclick a PATCH.
 */
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { MessageSquare, Paperclip, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  PAGE_SIZE,
  useAdminTicketSummary,
  useAdminTickets,
  useAdminUsers,
  type AdminTicketFilters,
} from '@/api/admin'
import { useAuth } from '@/auth/AuthProvider'
import { Pagination, TableScroller, Td, Th, formatDateTime } from '@/components/admin/AdminShell'
import {
  TicketPriorityPill,
  TicketStatusPill,
  TicketTypeIcon,
} from '@/components/support/TicketBadges'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/cn'

const TABS = ['awaiting', 'mine', 'open', 'in_progress', 'resolved', 'closed', 'all'] as const
type Tab = (typeof TABS)[number]

const TYPES = ['issue', 'feature', 'question'] as const
const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const

const selectClass = 'h-9 rounded-md border border-border bg-surface px-2 text-sm text-text'

function filtersFor(tab: Tab, me: number | undefined): AdminTicketFilters {
  switch (tab) {
    case 'awaiting':
      return { status: 'active', awaiting: true }
    case 'mine':
      return { status: 'active', assignee_id: me }
    case 'all':
      return {}
    default:
      return { status: tab }
  }
}

export function AdminTickets() {
  const { t } = useTranslation(['admin', 'common'])
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()

  const rawTab = params.get('tab')
  const tab: Tab = TABS.includes(rawTab as Tab) ? (rawTab as Tab) : 'awaiting'
  const [type, setType] = useState('')
  const [priority, setPriority] = useState('')
  const [assignee, setAssignee] = useState('')
  const [q, setQ] = useState('')
  const [offset, setOffset] = useState(0)

  const summary = useAdminTicketSummary()
  const staff = useAdminUsers({ system_role: 'sysadmin' })
  const tickets = useAdminTickets({
    ...filtersFor(tab, user?.id),
    type: type || undefined,
    priority: priority || undefined,
    ...(tab !== 'mine' && assignee ? { assignee_id: Number(assignee) } : {}),
    q: q || undefined,
    offset,
  })

  const counts: Partial<Record<Tab, number>> = summary.data
    ? {
        awaiting: summary.data.awaiting,
        mine: summary.data.mine,
        open: summary.data.open,
        in_progress: summary.data.in_progress,
        resolved: summary.data.resolved,
        closed: summary.data.closed,
      }
    : {}

  const selectTab = (next: Tab) => {
    setParams(next === 'awaiting' ? {} : { tab: next })
    setOffset(0)
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{t('admin.tickets.title')}</h2>

      <div
        className="flex gap-1 overflow-x-auto"
        role="tablist"
        aria-label={t('common.status', { ns: 'common' })}
      >
        {TABS.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            className={cn(
              'flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-sm',
              tab === value
                ? 'bg-surface-raised font-medium text-text'
                : 'text-text-muted hover:text-text',
            )}
            onClick={() => selectTab(value)}
          >
            {t(`admin.tickets.tab.${value}`)}
            {counts[value] !== undefined && (
              <span
                className={cn(
                  'rounded-full px-1.5 text-[11px] tabular',
                  value === 'awaiting' && counts[value]
                    ? 'bg-accent text-accent-fg'
                    : 'bg-surface-raised text-text-muted',
                )}
              >
                {counts[value]}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search
            size={14}
            aria-hidden
            className="absolute start-2.5 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <Input
            className="h-9 w-56 ps-8 text-sm"
            placeholder={t('admin.tickets.search_placeholder')}
            aria-label={t('action.search', { ns: 'common' })}
            value={q}
            onChange={(event) => {
              setQ(event.target.value)
              setOffset(0)
            }}
          />
        </div>
        <select
          className={selectClass}
          aria-label={t('support.type', { ns: 'common' })}
          value={type}
          onChange={(event) => {
            setType(event.target.value)
            setOffset(0)
          }}
        >
          <option value="">{t('admin.tickets.any_type')}</option>
          {TYPES.map((value) => (
            <option key={value} value={value}>
              {t(`support.type.${value}`, { ns: 'common' })}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          aria-label={t('admin.tickets.priority')}
          value={priority}
          onChange={(event) => {
            setPriority(event.target.value)
            setOffset(0)
          }}
        >
          <option value="">{t('admin.tickets.any_priority')}</option>
          {PRIORITIES.map((value) => (
            <option key={value} value={value}>
              {t(`support.priority.${value}`, { ns: 'common' })}
            </option>
          ))}
        </select>
        {tab !== 'mine' && (
          <select
            className={selectClass}
            aria-label={t('admin.tickets.assignee')}
            value={assignee}
            onChange={(event) => {
              setAssignee(event.target.value)
              setOffset(0)
            }}
          >
            <option value="">{t('admin.tickets.any_assignee')}</option>
            <option value="0">{t('admin.tickets.unassigned')}</option>
            {staff.data?.items.map((person) => (
              <option key={person.id} value={person.id}>
                {person.display_name}
              </option>
            ))}
          </select>
        )}
      </div>

      {tickets.data && tickets.data.items.length === 0 ? (
        <p className="rounded-lg border border-border p-6 text-center text-sm text-text-muted">
          {t('admin.tickets.empty')}
        </p>
      ) : (
        <>
          <TableScroller>
            <thead>
              <tr>
                <Th>{t('support.subject', { ns: 'common' })}</Th>
                <Th>{t('admin.tickets.reporter')}</Th>
                <Th>{t('common.status', { ns: 'common' })}</Th>
                <Th>{t('admin.tickets.priority')}</Th>
                <Th>{t('admin.tickets.assignee')}</Th>
                <Th>{t('admin.tickets.last_activity')}</Th>
              </tr>
            </thead>
            <tbody>
              {tickets.data?.items.map((row) => (
                <tr
                  key={row.id}
                  className={cn(
                    row.awaiting_staff && row.status !== 'closed' && 'bg-accent-subtle/40',
                  )}
                >
                  <Td>
                    <div className="flex items-start gap-2">
                      <TicketTypeIcon type={row.type} size={15} />
                      <div className="min-w-0">
                        <Link
                          to={`/admin/tickets/${row.id}`}
                          className="font-medium text-text hover:underline"
                        >
                          {row.title}
                        </Link>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-muted">
                          {row.module_id && <span className="font-mono">{row.module_id}</span>}
                          {row.message_count > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <MessageSquare size={11} aria-hidden />
                              {row.message_count}
                            </span>
                          )}
                          {row.attachment_count > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <Paperclip size={11} aria-hidden />
                              {row.attachment_count}
                            </span>
                          )}
                          {row.awaiting_staff && row.status !== 'closed' && (
                            <span className="font-medium text-accent">
                              {t('admin.tickets.needs_reply')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Td>
                  <Td className="text-text-muted">
                    <div className="text-text">
                      {row.reporter_display_name ?? row.reporter_username ?? '—'}
                    </div>
                    <div className="text-xs">{row.workspace_name ?? '—'}</div>
                  </Td>
                  <Td>
                    <TicketStatusPill status={row.status} />
                  </Td>
                  <Td>
                    <TicketPriorityPill priority={row.priority} />
                  </Td>
                  <Td className="text-text-muted">
                    {row.assignee_display_name ?? t('admin.tickets.unassigned')}
                  </Td>
                  <Td className="whitespace-nowrap text-text-muted tabular">
                    {formatDateTime(row.last_activity_at)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableScroller>

          <Pagination
            total={tickets.data?.total ?? 0}
            limit={PAGE_SIZE}
            offset={offset}
            onChange={setOffset}
          />
        </>
      )}
    </div>
  )
}
